import { isNil } from 'lodash'
import {
  compact,
  concat,
  includes,
  isEmpty,
  join,
  keys,
  map,
  reduce,
  size,
  split
} from 'lodash/fp'
import React from 'react'
import {
  Form,
  type Identifier,
  NotFound,
  type RaRecord,
  type RowClickFunction,
  ShowBase,
  SimpleShowLayout,
  TextField,
  required,
  useRecordContext,
  useTranslate
} from 'react-admin'
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  Radio,
  RadioGroup,
  Tab,
  Tabs
} from '@mui/material'
import {
  format,
  isAfter,
  isBefore,
  parseISO,
  startOfMonth,
  subDays
} from 'date-fns/fp'
import {
  ElectricityChartDaily,
  ElectricityChartMonthly,
  ElectricityChartYearly
} from 'components/Chart'
import log from 'loglevel'
import { DateInput, type ScrollProps } from 'components/Util'
import { SpotSelect } from 'components/SpotSelect'
import { type ConfigProps } from 'components/Dashboard'
import {
  ElectricityChartDailyKind,
  ElectricityChartMonthlyKind,
  ElectricityChartYearlyKind
} from '../../../../cdk/bapp/lib/lambda-api/src/types'

log.setLevel((process.env['LOG_LEVEL'] as log.LogLevelDesc) ?? log.levels.DEBUG)

const SpotShowContent: React.FC<
  {
    partner: string
    date: string
    startMonthOfFiscalYear: string
  } & ConfigProps &
    ScrollProps
> = ({
  partner,
  date,
  config,
  configChart,
  configMappings,
  scrollRef,
  startMonthOfFiscalYear
}) => {
  const translate = useTranslate()
  const { tabs, kinds: configKinds, showAmount, showDemand } = config
  const record = useRecordContext()
  const { spin, classification, kind } = record
  const compareToKinds = [
    [
      ElectricityChartDailyKind.lastWeek,
      ElectricityChartDailyKind.lastYearWithMonthlyAverage,
      ElectricityChartDailyKind.lastYearWithYearlyAverage
    ],
    configKinds.monthly ?? [
      ElectricityChartMonthlyKind.lastYear,
      ElectricityChartMonthlyKind.lastMonth,
      ElectricityChartMonthlyKind.lastYearWithMonthlyAverage
    ],
    configKinds.yearly ?? [ElectricityChartYearlyKind.lastYear]
  ]
  const [tab, setTab] = React.useState<number>(0)
  const [compareTo, setCompareTo] = React.useState<
    | ElectricityChartDailyKind
    | ElectricityChartMonthlyKind
    | ElectricityChartYearlyKind
    | undefined
  >((compareToKinds[tab] as any[])[0])
  const kinds = [['高圧'], ['低圧'], ['高圧', '低圧']]
  const [showKinds, setShowKinds] = React.useState<string[]>(
    kinds[/高圧/.test(kind) ? (/低圧/.test(kind) ? 2 : 0) : 1] as string[]
  )
  const handleChange = (
    _event: React.SyntheticEvent,
    newValue: number
  ): void => {
    setTab(newValue)
    setCompareTo(
      (
        compareToKinds[newValue] as Array<
          | ElectricityChartDailyKind
          | ElectricityChartMonthlyKind
          | ElectricityChartYearlyKind
        >
      )[0]
    )
  }
  let targets: string[] = []
  const { useTarget } = config
  const { area, office, subs } = configMappings
  if (!isNil(useTarget) && (useTarget as boolean)) {
    targets = concat([area, office])(
      !isNil(subs) ? map((sub: string) => sub)(subs) : []
    )
  }
  const targetsPlus = concat(targets)(['addressState', 'addressCity'])
  const [showTargets, setShowTargets] = React.useState(
    !isNil(useTarget) && (useTarget as boolean)
      ? reduce(
          (sum: Record<string, boolean>, target: string) => ({
            ...sum,
            [target]: target === area || target === office
          }),
          {}
        )(targetsPlus)
      : undefined
  )
  const SpotChartControl: React.FC<{
    mode: string
    compareToKinds: string[]
  }> = ({ mode, compareToKinds }) => {
    const handleCompareToChange = (
      _event: React.SyntheticEvent,
      newValue: string
    ): void => {
      setCompareTo(
        newValue as
          | ElectricityChartDailyKind
          | ElectricityChartMonthlyKind
          | ElectricityChartYearlyKind
      )
    }
    const handleTargetsChange = (
      event: React.ChangeEvent<HTMLInputElement>
    ): void => {
      setShowTargets({
        ...showTargets,
        [event.target.name]: event.target.checked
      })
    }
    const handleKindsChange = (
      _event: React.SyntheticEvent,
      newValue: string
    ): void => {
      setShowKinds(split(',')(newValue))
    }
    return (
      <FormControl sx={{ ml: '0.5em' }}>
        <RadioGroup
          row
          value={compareTo}
          onChange={handleCompareToChange}
          sx={{ ml: '0.5em' }}>
          {/* @ts-expect-error ts(23339) */}
          {map.convert({ cap: false })((kind: string, index: number) => (
            <FormControlLabel
              key={`SpotShowContent.FormControl.RadioGroup1.FormControlLabel.${index}`}
              value={kind}
              control={<Radio />}
              label={translate(`resources.spots.${mode}.compareTo.${kind}`)}
              disabled={size(compareToKinds) === 1}
            />
          ))(compareToKinds)}
        </RadioGroup>
        {!isNil(targets) && !isNil(showTargets) && (
          <FormGroup row sx={{ ml: '0.5em' }}>
            {/* @ts-expect-error ts(23339) */}
            {map.convert({ cap: false })((target: string, index: number) => {
              return (
                <FormControlLabel
                  key={`SpotShowContent.FormControl.RadioGroup2.FormControlLabel.${index}`}
                  control={
                    <Checkbox
                      onChange={handleTargetsChange}
                      checked={showTargets[target]}
                      name={target}
                    />
                  }
                  label={`${
                    target === 'addressState'
                      ? '都道府県'
                      : target === 'addressCity'
                      ? '市区町村'
                      : target
                  }平均`}
                />
              )
            })(targetsPlus)}
            {/* <FormControlLabel
              control={
                <Checkbox
                  onChange={handleTargetsChange}
                  checked={showTargets}
                />
              }
              label={`${join('、')(targets)}を表示`}
            /> */}
          </FormGroup>
        )}
        {!isNil(kinds) && !isNil(useTarget) && (useTarget as boolean) && (
          <RadioGroup
            row
            value={showKinds}
            onChange={handleKindsChange}
            sx={{ ml: '0.5em' }}>
            {/* @ts-expect-error ts(23339) */}
            {map.convert({ cap: false })((kind: string[], index: number) => {
              return (
                <FormControlLabel
                  key={`SpotShowContent.FormControl.RadioGroup3.FormControlLabel.${index}`}
                  value={join(',')(kind)}
                  control={<Radio />}
                  label={`${
                    includes('高圧')(kind) && includes('低圧')(kind)
                      ? '全電圧'
                      : (kind[0] as string)
                  }平均`}
                />
              )
            })(kinds)}
          </RadioGroup>
        )}
      </FormControl>
    )
  }
  React.useEffect(() => {
    if (isNil(kinds)) return
    const { kind } = record
    setShowKinds(
      kinds[/高圧/.test(kind) ? (/低圧/.test(kind) ? 2 : 0) : 1] as string[]
    )
  }, [record])
  // React.useEffect(() => {
  //   if (!isNil(scrollRef.current)) scrollRef.current.scrollIntoView()
  // })
  if (isNil(spin)) return <NotFound />
  if (isNil(date) || isEmpty(date)) return <></>
  return (
    <Grid container spacing={2}>
      <Grid item xs={8}>
        <Card>
          <Tabs value={tab} onChange={handleChange}>
            {includes('daily')(tabs) && (
              <Tab label={translate('resources.spots.tabs.daily')} />
            )}
            {includes('monthly')(tabs) && (
              <Tab label={translate('resources.spots.tabs.monthly')} />
            )}
            {includes('yearly')(tabs) && (
              <Tab label={translate('resources.spots.tabs.yearly')} />
            )}
          </Tabs>
          {tab === 0 && (
            <Box>
              <SpotChartControl
                mode="daily"
                compareToKinds={
                  configKinds.daily ?? [
                    'lastWeek',
                    'lastYearWithMonthlyAverage',
                    'lastYearWithYearlyAverage'
                  ]
                }
              />
              <ElectricityChartDaily
                spin={spin}
                partner={partner}
                date={date}
                compareTo={compareTo as ElectricityChartDailyKind}
                targets={showTargets ?? undefined}
                scrollRef={scrollRef}
                configChart={configChart}
                configMappings={configMappings}
                record={record}
                classsification={classification}
                showKinds={showKinds}
                amountsId={
                  !isNil(showAmount) && (showAmount as boolean)
                    ? 'amounts'
                    : undefined
                }
                demandId={
                  !isNil(showDemand) &&
                  (showDemand as boolean) &&
                  /高圧/.test(record['kind']) &&
                  (isNil(classification) || classification === '供給')
                    ? 'demand'
                    : undefined
                }
              />
            </Box>
          )}
          {tab === 1 && (
            <Box>
              <SpotChartControl
                mode="monthly"
                compareToKinds={
                  configKinds.monthly ?? [
                    'lastYear',
                    'lastMonth',
                    'lastYearWithMonthlyAverage'
                  ]
                }
              />
              <ElectricityChartMonthly
                spin={spin}
                partner={partner}
                date={format('yyyy-MM-dd')(startOfMonth(parseISO(date)))}
                inputDate={date}
                compareTo={compareTo as ElectricityChartMonthlyKind}
                targets={showTargets ?? undefined}
                configChart={configChart}
                configMappings={configMappings}
                record={record}
                classification={classification}
                showKinds={showKinds}
                amountsId={
                  !isNil(showAmount) && (showAmount as boolean)
                    ? 'amounts'
                    : undefined
                }
                demandId={
                  !isNil(showDemand) &&
                  (showDemand as boolean) &&
                  /高圧/.test(record['kind'])
                    ? 'demand'
                    : undefined
                }
              />
            </Box>
          )}
          {tab === 2 && (
            <Box>
              <SpotChartControl
                mode="yearly"
                compareToKinds={configKinds.yearly ?? ['lastYear']}
              />
              <ElectricityChartYearly
                spin={spin}
                partner={partner}
                date={
                  isBefore(
                    parseISO(
                      `${parseISO(date).getFullYear()}-${
                        startMonthOfFiscalYear as string
                      }-01`
                    )
                  )(parseISO(date))
                    ? `${parseISO(date).getFullYear() - 1}-${
                        startMonthOfFiscalYear as string
                      }-01`
                    : `${parseISO(date).getFullYear()}-${
                        startMonthOfFiscalYear as string
                      }-01`
                }
                inputDate={date}
                compareTo={compareTo as ElectricityChartYearlyKind}
                configChart={configChart}
                targets={showTargets ?? undefined}
                configMappings={configMappings}
                record={record}
                classification={classification}
                showKinds={showKinds}
                amountsId={
                  !isNil(showAmount) && (showAmount as boolean)
                    ? 'amounts'
                    : undefined
                }
                demandId={
                  !isNil(showDemand) &&
                  (showDemand as boolean) &&
                  /高圧/.test(record['kind'])
                    ? 'demand'
                    : undefined
                }
              />
            </Box>
          )}
        </Card>
      </Grid>
      <Grid item xs={4}>
        {((!isNil(showAmount) && (showAmount as boolean)) ||
          (!isNil(showDemand) && (showDemand as boolean))) && (
          <Grid container spacing={2}>
            {!isNil(showAmount) && (showAmount as boolean) && (
              <Grid
                item
                xs={
                  !isNil(showDemand) &&
                  (showDemand as boolean) &&
                  /高圧/.test(record['kind'])
                    ? 6
                    : 12
                }>
                <Card>
                  <div id="amounts" />
                </Card>
              </Grid>
            )}
            {!isNil(showDemand) &&
            (showDemand as boolean) &&
            /高圧/.test(record['kind']) ? (
              <Grid
                item
                xs={!isNil(showAmount) && (showAmount as boolean) ? 6 : 12}>
                <Card>
                  <div id="demand" />
                </Card>
              </Grid>
            ) : (
              <></>
            )}
          </Grid>
        )}
        {!isNil(showDemand) &&
          (showDemand as boolean) &&
          /高圧/.test(record['kind']) && (
            <Card>
              <div id="demandHint" />
            </Card>
          )}
        <Card
          sx={{
            mt:
              (!isNil(showAmount) && (showAmount as boolean)) ||
              (!isNil(showDemand) && (showDemand as boolean))
                ? '1em'
                : '0em'
          }}>
          <SimpleShowLayout>
            {map((key: string) => {
              if (key === 'spin' && size(split(',')(spin)) > 1) {
                // @ts-expect-error ts(2339)
                return map.convert({ cap: false })(
                  (eachSpin: string, index: number) => {
                    return (
                      <TextField
                        key={`${key}-${index}`}
                        record={{ spin: eachSpin }}
                        source={key}
                        label={
                          index === 0
                            ? config.fields[key].label ??
                              translate(`resources.mergedUsers.fields.${key}`)
                            : false
                        }
                      />
                    )
                  }
                )(split(',')(spin))
                // }
                // if (key === 'kind' && size(split('/')(kind)) > 1) {
                //   const kinds = join('/')(uniq(split('/')(kind)))
                //   // @ts-expect-error ts(2339)
                //   return map.convert({ cap: false })(
                //     (eachKind: string, index: number) => {
                //       return (
                //         <TextField
                //           key={`${key}-${index}`}
                //           record={{ kind: eachKind }}
                //           source={key}
                //           label={
                //             index === 0
                //               ? config.fields[key].label ??
                //                 translate(`resources.mergedUsers.fields.${key}`)
                //               : false
                //           }
                //         />
                //       )
                //     }
                //   )(split('/')(kinds))
              } else {
                return (
                  <TextField
                    key={key}
                    source={key}
                    label={
                      config.fields[key].label ??
                      translate(`resources.mergedUsers.fields.${key}`)
                    }
                  />
                )
              }
            })(compact(keys(config.fields)))}
          </SimpleShowLayout>
        </Card>
      </Grid>
    </Grid>
  )
}

export const SpotShow: React.FC<
  {
    target: string
    partner: string
    date: string
    startMonthOfFiscalYear: string
  } & ScrollProps &
    ConfigProps
> = ({
  target,
  partner,
  scrollRef,
  config,
  configChart,
  configMappings,
  date,
  startMonthOfFiscalYear
}) => {
  return (
    <ShowBase resource="mappings" id={target}>
      <SpotShowContent
        partner={partner}
        date={date}
        config={config}
        configChart={configChart}
        configMappings={configMappings}
        scrollRef={scrollRef}
        startMonthOfFiscalYear={startMonthOfFiscalYear}
      />
    </ShowBase>
  )
}
export const Spot: React.FC<
  { partner: string } & ScrollProps & ConfigProps
> = ({ config, partner, scrollRef }) => {
  const {
    Spot: configSpot,
    Chart: configChart,
    Mappings: configMappings,
    startMonthOfFiscalYear,
    maxDate,
    Limits: configLimits
  } = config
  const translate = useTranslate()
  const [show, setShow] = React.useState(false)
  const [target, setTarget] = React.useState<string | undefined>(undefined)
  const [targetDate, setTargetDate] = React.useState<string>(
    !isNil(maxDate)
      ? maxDate
      : isNil(process.env['INPUT'])
      ? format('yyyy-MM-dd')(
          isAfter(parseISO(`${format('yyyy-MM-dd')(Date.now())}T15:00`))(
            Date.now()
          )
            ? subDays(1)(Date.now())
            : subDays(2)(Date.now())
        )
      : '2024-12-12'
  )
  const handleRowClick: RowClickFunction = (
    id: Identifier,
    _resource: string,
    _record: RaRecord
  ): any => {
    setTarget(id as string)
    setShow(true)
    if (!isNil(scrollRef) && !isNil(scrollRef.current)) {
      scrollRef.current.scrollIntoView()
    }
    return false
  }
  const { start } = configLimits ?? { start: undefined }
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          flexDirection: 'row'
        }}>
        <SpotSelect
          rowClick={handleRowClick}
          opened={isNil(target)}
          config={config}
        />
        <Form
          defaultValues={{
            date: targetDate
          }}>
          <DateInput
            source="date"
            label={translate('resources.spots.fields.date')}
            validate={required()}
            disableFuture
            minDate={!isNil(start) ? parseISO(start) : undefined}
            maxDate={
              !isNil(maxDate)
                ? parseISO(maxDate)
                : isAfter(
                    parseISO(`${format('yyyy-MM-dd')(Date.now())}T15:00`)
                  )(Date.now())
                ? subDays(1)(Date.now())
                : subDays(2)(Date.now())
            }
            views={['year', 'month', 'day']}
            sx={{ width: 250 }}
            onChange={(_source: string, value: string): void => {
              setTargetDate(value)
            }}
          />
        </Form>
      </Box>
      {show && !isNil(target) && (
        <Card sx={{ mt: '1em' }}>
          <CardContent>
            <SpotShow
              target={target}
              partner={partner}
              scrollRef={scrollRef}
              config={configSpot}
              configChart={configChart}
              configMappings={configMappings}
              date={targetDate}
              startMonthOfFiscalYear={startMonthOfFiscalYear}
            />
          </CardContent>
        </Card>
      )}
    </>
  )
}
